// The website's analysis tab: the website's state first (last real analysis), a stable way to prepare a
// new one, and — when the latest finished run was a check of corrections — a compact banner for that
// check (not a second dashboard). While an analysis runs, its progress; the estimate and earlier runs
// stay secondary. A new analysis always starts from the summary; reading or refreshing starts nothing.
import { ArrowRightIcon, PlusIcon } from '@radix-ui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { type Ref, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Scan, type Site, scanIssuesQuery, scanQuery, scansQuery } from '../api';
import { scannedFamilies } from '../page-families';
import { ReportButton } from '../report-button';
import { groupProblems, isFullyDiscarded, problemsByPage, splitCategories } from '../results-logic';
import { byWorst, comparableTrend } from '../scan-logic';
import { Button, buttonClass, Message, useArrivalFocus, usePageTitle } from '../ui';
import { TrendChart } from '../viz';
import { CheckOutcome } from './check-result';
import { PrepareAnalysis } from './prepare';
import { AnalysisSummary, CountingDetails, Honesty, ScoreDetails } from './results';
import { LiveScanView } from './scan-live';
import { PageFamilyCard, useDate } from './scan-parts';
import {
  FailedStatusCard,
  OutcomeIcon,
  PartialNotice,
  RefreshError,
  refreshOf,
  ScanStatusRegion,
  StatusTag,
  useScanAnnouncement,
} from './scan-status';

export { ScanPage } from './scan-page';

/** The latest check of corrections: a compact banner under the website's analysis. */
function LatestCheck({ scan, siteId }: { scan: Scan; siteId: string }) {
  const { t } = useTranslation();
  const detail = useQuery(scanQuery(scan.id));
  if (detail.data) return <CheckOutcome scan={detail.data} siteId={siteId} seeResults compact />;
  return detail.isError ? (
    <Message tone="error">{t('errors.generic')}</Message>
  ) : (
    <p role="status">{t('app.loading')}</p>
  );
}

/** Pages shown under the latest result: the ones that need attention first. */
const PAGES_SHOWN = 8;

/**
 * The website's latest analysis: its date with the state beside it, the summary, the pages analysed,
 * and the information about the analysis in a secondary section. Preparing a new analysis lives in the
 * tab toolbar, not here.
 */
function LatestResult({
  scan,
  siteId,
  current,
  mayScan = false,
  headInToolbar = false,
}: {
  scan: Scan;
  siteId: string;
  current: boolean;
  /** The person's role may start analyses (and complete a partial full one). */
  mayScan?: boolean;
  /** Title and meta already sit beside «Preparar un nuevo análisis». */
  headInToolbar?: boolean;
}) {
  const { t } = useTranslation();
  const date = useDate();
  const detail = useQuery(scanQuery(scan.id));
  const issues = useQuery(scanIssuesQuery(scan.id));
  const pages = detail.data ? [...detail.data.pages].sort(byWorst) : [];
  // The language versions of a page are one card, with a selector for the language.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `pages` is derived from the scan detail.
  const families = useMemo(() => scannedFamilies(pages), [detail.data]);
  const problems = useMemo(
    () =>
      groupProblems(splitCategories(issues.data ?? []).problems).filter(
        (problem) => !isFullyDiscarded(problem),
      ),
    [issues.data],
  );
  const found = useMemo(() => problemsByPage(problems, detail.data?.pages ?? []), [problems, detail.data]);
  const title = t(current ? 'scans.latestTitle' : 'scans.lastAvailableTitle');
  return (
    <article className="latest-result" aria-labelledby="ultimo-analisis">
      {headInToolbar ? (
        <h3 id="ultimo-analisis" className="sr-only">
          {title}
        </h3>
      ) : (
        <header className="latest-head">
          <div className="latest-title">
            <h3 id="ultimo-analisis">{title}</h3>
            <p className="latest-meta">
              <span>
                {t('scans.meta', { date: date(scan.createdAt), type: t(`scans.types.${scan.type}`) })}
              </span>
              <StatusTag outcome={scan.outcome} />
            </p>
          </div>
          {!current && (
            <Link
              to="/sites/$siteId/scans/$scanId"
              params={{ siteId, scanId: scan.id }}
              className={buttonClass('secondary')}
            >
              {t('status.actions.previousResults')}
              <ArrowRightIcon aria-hidden="true" />
            </Link>
          )}
        </header>
      )}
      {scan.outcome === 'partial' && (
        <PartialNotice scan={scan} pages={detail.data?.pages} siteId={siteId} mayComplete={mayScan} />
      )}
      {detail.data && issues.data ? (
        <>
          <AnalysisSummary
            problems={problems}
            pages={detail.data.pages}
            discarded={detail.data.discarded}
            headingLevel={4}
            explain={false}
            action={
              <Link
                to="/sites/$siteId/scans/$scanId"
                params={{ siteId, scanId: scan.id }}
                className={buttonClass('secondary')}
              >
                {t('scans.seeAllProblems')}
                <ArrowRightIcon aria-hidden="true" />
              </Link>
            }
          />
          {pages.length > 0 && (
            <section className="latest-pages" aria-labelledby="ultimas-paginas">
              <h4 id="ultimas-paginas">{t('scans.pagesHeading')}</h4>
              <ul className="page-cards">
                {families.slice(0, PAGES_SHOWN).map((family) => (
                  <li key={family.key}>
                    <PageFamilyCard
                      scanId={scan.id}
                      siteId={siteId}
                      family={family}
                      found={(key) => found.get(key)}
                      shortStatus
                    />
                  </li>
                ))}
              </ul>
              {families.length > PAGES_SHOWN && (
                <Link
                  to="/sites/$siteId/paginas"
                  params={{ siteId }}
                  search={{ scan: scan.id }}
                  className="report-link"
                >
                  {t('scans.allPages', { count: pages.length })}
                  <ArrowRightIcon aria-hidden="true" />
                </Link>
              )}
            </section>
          )}
          <section className="analysis-info" aria-labelledby="sobre-analisis">
            <h4 id="sobre-analisis">{t('scans.about')}</h4>
            <Honesty />
            <ScoreDetails scan={detail.data} showMethod={false} />
            <CountingDetails problems={problems} pages={detail.data.pages} />
            <div className="mt-5">
              <ReportButton scanId={scan.id} />
            </div>
          </section>
        </>
      ) : issues.isError ? (
        <Message tone="error">{t('errors.generic')}</Message>
      ) : (
        <p role="status">{t('app.loading')}</p>
      )}
    </article>
  );
}

/** Stable way to prepare a new analysis: always at the top of the tab when it is available. */
function AnalysisToolbar({
  onPrepare,
  prepareRef,
  empty,
  latest,
}: {
  onPrepare: () => void;
  prepareRef?: Ref<HTMLButtonElement>;
  /** First analysis: the empty-state label. */
  empty?: boolean;
  /** Latest finished analysis: its title, date, type and state sit beside the button. */
  latest?: Scan;
}) {
  const { t } = useTranslation();
  const date = useDate();
  return (
    <div className="analysis-toolbar">
      <Button
        ref={prepareRef}
        onPress={onPrepare}
        variant="primary"
        className={empty ? 'analysis-toolbar-action' : 'analysis-toolbar-action analysis-toolbar-prepare'}
      >
        {!empty && <PlusIcon aria-hidden="true" />}
        {t(empty ? 'scans.prepare.action' : 'scans.prepareAgain')}
      </Button>
      {latest && (
        <div className="analysis-toolbar-latest">
          <p className="analysis-toolbar-latest-title">{t('scans.latestTitle')}</p>
          <p className="latest-meta">
            <span>
              {t('scans.meta', { date: date(latest.createdAt), type: t(`scans.types.${latest.type}`) })}
            </span>
            <StatusTag outcome={latest.outcome} />
          </p>
        </div>
      )}
    </div>
  );
}

export function ScansSection({
  site,
  mayScan,
  mayConfigure,
  onVerify,
  autoPrepare = false,
  check,
  onAutoPrepared,
}: {
  site: Site;
  mayScan: boolean;
  /** The person's role can open the website's settings. */
  mayConfigure: boolean;
  /** Leads to the ownership verification; absent when the user's role cannot verify. */
  onVerify?: () => void;
  /** Open the summary for a new analysis as soon as the state is known (from a link in a report). */
  autoPrepare?: boolean;
  /** The elements whose fix is being checked: the summary says which pages that would analyse. */
  check?: string[];
  onAutoPrepared?: () => void;
}) {
  const { t } = useTranslation();
  const date = useDate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scans = useQuery(scansQuery(site.id));
  const latest = scans.data?.[0];
  const active = latest?.outcome === 'queued' || latest?.outcome === 'running';
  // The pages and the live state of the latest analysis (the list only has its state).
  const detail = useQuery({ ...scanQuery(latest?.id ?? ''), enabled: Boolean(latest) });
  const refresh = refreshOf(scans, ...(latest ? [detail] : []));
  // The summary before an analysis: opening it starts nothing; its own button does.
  const [preparing, setPreparing] = useState(false);
  // The elements to check, kept when the address is cleaned up after the summary opens.
  const [checking, setChecking] = useState<string[]>([]);
  const prepareButton = useRef<HTMLButtonElement>(null);
  const announcement = useScanAnnouncement(site.id, latest, scans.isSuccess, refresh.failed);
  const lastDone = scans.data?.find((scan) => scan.status === 'done');
  // A check of corrections only looked at some pages: the website's analysis is the last one that is not a check.
  const lastFull = scans.data?.find((scan) => scan.status === 'done' && scan.type !== 'comprobacion');
  const recentCheck = lastDone?.type === 'comprobacion' ? lastDone : undefined;
  const heading = useArrivalFocus();
  usePageTitle(t('scans.title'), site.name);

  function openPrepare(elements: string[] = []) {
    setChecking(elements);
    setPreparing(true);
  }

  // When the analysis in progress ends, the result is read again, fresh.
  const wasActive = useRef(active);
  useEffect(() => {
    if (wasActive.current && !active && latest) {
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      void queryClient.invalidateQueries({ queryKey: scanQuery(latest.id).queryKey });
    }
    wasActive.current = active;
  }, [active, latest, queryClient]);

  // A link that asks for the summary (the report of a failed analysis): open it once, when it fits.
  const autoPrepared = useRef(false);
  useEffect(() => {
    if (!autoPrepare || !scans.isSuccess || autoPrepared.current) return;
    autoPrepared.current = true;
    if (!active) {
      setChecking(check ?? []);
      setPreparing(true);
    }
    onAutoPrepared?.();
  }, [autoPrepare, scans.isSuccess, active, check, onAutoPrepared]);

  // The system accepted the request: the summary gives way to the state of the analysis, which takes
  // the focus because the button the person pressed is gone.
  const [focusStatus, setFocusStatus] = useState(false);
  async function started() {
    setPreparing(false);
    setFocusStatus(true);
    await queryClient.invalidateQueries({ queryKey: scansQuery(site.id).queryKey });
  }
  useEffect(() => {
    if (!focusStatus || !active) return;
    document.getElementById('live-title')?.focus();
    setFocusStatus(false);
  }, [focusStatus, active]);

  function closeSummary() {
    setPreparing(false);
    // The focus goes back to what opened it.
    requestAnimationFrame(() => prepareButton.current?.focus());
  }

  const summary = preparing ? (
    <PrepareAnalysis
      site={site}
      check={checking}
      onVerify={onVerify}
      onClose={closeSummary}
      onStarted={started}
    />
  ) : null;

  const showToolbar = mayScan && !preparing && !active;
  const trend = comparableTrend(scans.data ?? []);

  return (
    <section aria-labelledby="analisis" className="analysis">
      {/* The tab that led here already says "Análisis": the heading stays for screen readers and for the
          focus, and the screen starts with its content. */}
      <h2 ref={heading} id="analisis" tabIndex={-1} className="sr-only focus:outline-none">
        {t('scans.title')}
      </h2>
      {/* While preparing, only the wizard: previous results and history stay out of the way. */}
      {preparing ? (
        summary
      ) : (
        <>
          <ScanStatusRegion announcement={announcement} scan={latest} siteId={site.id} />
          {scans.isPending ? (
            <p>{t('app.loading')}</p>
          ) : !scans.data ? (
            <RefreshError refresh={refresh} />
          ) : !latest ? (
            <div className="analysis-empty">
              <h3 className="text-xl font-bold">{t('scans.emptyTitle')}</h3>
              <p>{t('scans.emptyText')}</p>
              {mayScan ? (
                <AnalysisToolbar empty prepareRef={prepareButton} onPrepare={() => openPrepare()} />
              ) : (
                <p className="text-sm text-ink-muted">{t('scans.errors.permiso')}</p>
              )}
            </div>
          ) : (
            <>
              {showToolbar && (
                <AnalysisToolbar
                  prepareRef={prepareButton}
                  onPrepare={() => openPrepare()}
                  latest={lastFull}
                />
              )}
              {active ? (
                <LiveScanView
                  scan={latest}
                  live={detail.data?.live ?? null}
                  url={site.url}
                  refresh={refresh}
                />
              ) : (
                <>
                  {refresh.failed && <RefreshError refresh={refresh} />}
                  {latest.outcome === 'failed' && (
                    <FailedStatusCard
                      scan={latest}
                      url={site.url}
                      siteId={site.id}
                      pages={detail.data?.pages}
                      mayConfigure={mayConfigure}
                      // Contextual retry on the failure card; the toolbar also offers a new analysis above.
                      onPrepare={mayScan ? () => openPrepare() : undefined}
                    />
                  )}
                </>
              )}
              {lastFull && (
                <LatestResult
                  scan={lastFull}
                  siteId={site.id}
                  mayScan={mayScan}
                  current={lastFull.id === latest.id || Boolean(recentCheck && recentCheck.id === latest.id)}
                  headInToolbar={showToolbar && Boolean(lastFull)}
                />
              )}
              {recentCheck && <LatestCheck scan={recentCheck} siteId={site.id} />}
              {scans.data.length > 1 && (
                <section className="history" aria-labelledby="historial">
                  <h3 id="historial">{t('scans.history')}</h3>
                  {trend && (
                    <div className="history-trend">
                      <h4>{t('scans.trend')}</h4>
                      <TrendChart
                        points={trend}
                        current={lastFull?.id}
                        onSelect={(scanId) =>
                          navigate({
                            to: '/sites/$siteId/scans/$scanId',
                            params: { siteId: site.id, scanId },
                          })
                        }
                      />
                    </div>
                  )}
                  <ul className="history-list">
                    {scans.data.slice(1).map((scan) => (
                      <li key={scan.id} data-outcome={scan.outcome} data-type={scan.type}>
                        <Link to="/sites/$siteId/scans/$scanId" params={{ siteId: site.id, scanId: scan.id }}>
                          {t('scans.historyItem', {
                            date: date(scan.createdAt),
                            type: t(`scans.types.${scan.type}`),
                          })}
                        </Link>
                        <span className="history-state">
                          <OutcomeIcon outcome={scan.outcome} />
                          {t(`status.outcomes.${scan.outcome}`)}
                        </span>
                        {scan.type === 'comprobacion' && (
                          <span className="history-kind">{t('scans.historyCheck')}</span>
                        )}
                        {scan.status === 'done' &&
                          scan.type !== 'comprobacion' &&
                          scan.scores?.accesibilidad.score != null && (
                            <span className="history-score">
                              {t('scans.historyScore', { score: scan.scores.accesibilidad.score })}
                            </span>
                          )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
